import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import ArticleCard from '@/components/base/ArticleCard';
import StatusBlock from '@/components/base/StatusBlock';
import { useAsyncData } from '@/hooks/useAsyncData';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { getAllArticles, searchArticles, sortArticles } from '@/lib/content';
import type { Article } from '@/types/content';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';
  const [input, setInput] = useState(keyword);

  useDocumentTitle(
    keyword ? `搜索“${keyword}” - 疯狂试爱2影视资讯` : '资讯搜索 - 疯狂试爱2',
    keyword ? `与“${keyword}”相关的疯狂试爱2影视资讯与剧情内容搜索结果。` : '搜索疯狂试爱2的影视资讯与剧情内容。',
  );

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const { data, loading, error, reload } = useAsyncData<Article[]>(
    () => (keyword ? searchArticles(keyword) : []),
    [keyword],
  );

  const hotList = sortArticles(getAllArticles(), 'hot').slice(0, 3);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    if (next) setSearchParams({ q: next });
  };

  return (
    <>
      <PageHeader
        eyebrow="SEARCH"
        title={keyword ? `“${keyword}” 的搜索结果` : '资讯搜索'}
        description="输入关键词，快速检索《疯狂试爱2》相关的影视资讯与剧情内容。"
        crumbs={[{ label: '首页', to: '/' }, { label: '资讯搜索' }]}
        extra={
          <form onSubmit={handleSubmit} className="flex w-full max-w-2xl flex-col gap-3 sm:flex-row">
            <div className="relative w-full">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
              <input
                type="text"
                name="q"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词，如 剧情、角色、预告片"
                className="h-12 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-12 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
            >
              重新搜索
            </button>
          </form>
        }
      />

      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
        {keyword && !loading && !error && (
          <p className="mb-6 text-sm text-foreground-600">
            找到 <strong className="font-heading text-base text-primary-600">{data?.length ?? 0}</strong> 条与
            “{keyword}”相关的内容
          </p>
        )}

        {loading && (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex flex-col gap-4 rounded-lg border border-background-200 p-4 sm:flex-row">
                <div className="h-40 w-full animate-pulse rounded-md bg-background-100 sm:h-32 sm:w-48"></div>
                <div className="flex-1 space-y-3 py-2">
                  <div className="h-4 w-2/3 animate-pulse rounded bg-background-100"></div>
                  <div className="h-3 w-full animate-pulse rounded bg-background-100"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <StatusBlock icon="ri-wifi-off-line" title="网络异常" description={error} actionLabel="重新加载" onAction={reload} />
        )}

        {!loading && !error && keyword && data && data.length === 0 && (
          <StatusBlock
            icon="ri-search-eye-line"
            title={`未找到与“${keyword}”相关内容`}
            description="换个关键词试试，或者看看下面这些热门内容。"
          />
        )}

        {!loading && !error && data && data.length > 0 && (
          <div className="space-y-4">
            {data.map((article) => (
              <ArticleCard key={article.id} article={article} variant="horizontal" />
            ))}
          </div>
        )}

        {!loading && !error && (!keyword || (data && data.length === 0)) && (
          <div className="mt-14">
            <h2 className="mb-6 font-heading text-lg font-bold text-foreground-950">热门内容推荐</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {hotList.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/news"
                className="inline-flex whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                浏览全部资讯
              </Link>
              <Link
                to="/category/plot"
                className="inline-flex whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                查看剧情介绍
              </Link>
            </div>
          </div>
        )}
      </section>
    </>
  );
}